import React, { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  RefreshCcw,
  Sparkles,
  X,
} from "lucide-react";
import {
  TASK_KIND,
  dismissTask,
  useStatusTasks,
} from "../stores/statusStore";

const KIND_LABEL = {
  [TASK_KIND.ANALYSIS]: "Analyzing",
  [TASK_KIND.ANALYSIS_BATCH]: "Batch analysis",
  [TASK_KIND.META_REFRESH]: "Refreshing Meta",
  [TASK_KIND.TAGGING]: "Applying tags",
  [TASK_KIND.CLUSTERING]: "Clustering concepts",
  [TASK_KIND.TRANSCRIBE]: "Transcribing video",
  [TASK_KIND.THESIS]: "Generating thesis",
  [TASK_KIND.CHAT]: "Chatting",
};

const KIND_ICON = {
  [TASK_KIND.ANALYSIS]: Activity,
  [TASK_KIND.ANALYSIS_BATCH]: Activity,
  [TASK_KIND.META_REFRESH]: RefreshCcw,
  [TASK_KIND.TAGGING]: Sparkles,
  [TASK_KIND.CLUSTERING]: Brain,
  [TASK_KIND.TRANSCRIBE]: FileText,
  [TASK_KIND.THESIS]: Sparkles,
  [TASK_KIND.CHAT]: Activity,
};

const formatProgress = (task) => {
  if (task.status === "error") return "failed";
  if (task.status === "done") return "done";
  if (task.total > 0) return `${task.current}/${task.total}`;
  return "running";
};

const StatusRow = ({ task }) => {
  const Icon = KIND_ICON[task.kind] || Activity;
  const pct =
    task.total > 0
      ? Math.max(0, Math.min(100, Math.round((task.current / task.total) * 100)))
      : null;
  return (
    <div className={`status-hub__row status-hub__row--${task.status}`}>
      <div className="status-hub__row-head">
        <span className="status-hub__row-icon">
          {task.status === "running" ? (
            <Loader2 size={13} className="status-hub__spin" />
          ) : task.status === "error" ? (
            <AlertTriangle size={13} />
          ) : (
            <CheckCircle2 size={13} />
          )}
        </span>
        <span className="status-hub__row-kind">
          <Icon size={12} /> {KIND_LABEL[task.kind] || task.kind}
        </span>
        <span className="status-hub__row-label" title={task.label}>
          {task.label}
        </span>
        <span className="status-hub__row-progress">{formatProgress(task)}</span>
        {task.status !== "running" ? (
          <button
            type="button"
            className="status-hub__row-dismiss"
            onClick={() => dismissTask(task.id)}
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        ) : null}
      </div>
      {pct != null ? (
        <div className="status-hub__bar">
          <div className="status-hub__bar-fill" style={{ width: `${pct}%` }} />
        </div>
      ) : null}
      {task.error ? (
        <div className="status-hub__error" title={task.error}>
          {task.error}
        </div>
      ) : null}
    </div>
  );
};

const StatusHub = () => {
  const { tasks } = useStatusTasks();
  const [expanded, setExpanded] = useState(false);

  const summary = useMemo(() => {
    const running = tasks.filter((t) => t.status === "running");
    const errors = tasks.filter((t) => t.status === "error");
    return { running, errors, total: tasks.length };
  }, [tasks]);

  if (summary.total === 0) return null;

  return (
    <div
      className={`status-hub${expanded ? " status-hub--expanded" : ""}${summary.errors.length ? " status-hub--errors" : ""}`}
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        className="status-hub__head"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="status-hub__head-icon">
          {summary.running.length ? (
            <Loader2 size={13} className="status-hub__spin" />
          ) : summary.errors.length ? (
            <AlertTriangle size={13} />
          ) : (
            <CheckCircle2 size={13} />
          )}
        </span>
        <span className="status-hub__head-text">
          {summary.running.length
            ? summary.running
                .slice(0, 2)
                .map((t) => `${KIND_LABEL[t.kind] || t.kind}${t.total > 0 ? ` ${t.current}/${t.total}` : ""}`)
                .join(" · ")
            : summary.errors.length
              ? `${summary.errors.length} task${summary.errors.length === 1 ? "" : "s"} failed`
              : "All tasks done"}
        </span>
        {summary.running.length > 2 ? (
          <span className="status-hub__head-more">+{summary.running.length - 2}</span>
        ) : null}
        <span className="status-hub__head-chev">
          {expanded ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
        </span>
      </button>
      {expanded ? (
        <div className="status-hub__list">
          {tasks.map((task) => (
            <StatusRow key={task.id} task={task} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default StatusHub;
